import styled from '@emotion/styled';
import { useListingContext } from '~/editor/plugin/ListingPlugin';
import {
	css_background,
	css_gap,
	css_medium_up,
	css_rail,
	css_rail_down,
	css_rail_up,
	css_small_down,
	css_stroke,
	css_text,
} from '~/emotion/theme';
import IconBedUltimate from '~/icon/ultimate/IconBedUltimate';
import IconBathUltimate from '~/icon/ultimate/IconBathUltimate';
import IconSqftUltimate from '~/icon/ultimate/IconSqftUltimate';

const Section = styled.section`
	background: ${css_background()};
	color: ${css_text()};
	border-bottom: 1px solid ${css_stroke()};
`;

const Inner = styled.div`
	display: flex;
	flex-direction: column;
	padding-left: ${(props) => props.theme.margin.left};
	padding-right: ${(props) => props.theme.margin.right};
	gap: ${css_gap('content')};

	@media (${css_rail_down}) {
		padding-top: ${css_gap('content')};
		padding-bottom: ${css_gap('content')};
	}

	@media (${css_medium_up}) {
		flex-direction: row;
	}
`;

const Summary = styled.div`
	flex: 1;

	@media (${css_rail_up}) {
		padding: ${css_gap('content')};
		padding-left: 0;
	}
`;

const Title = styled.h1`
	font-size: 2.5rem;
	font-weight: 500;
	margin: 0;
	text-align: left;
`;

const Subtitle = styled.h2`
	font-size: 1.125rem;
	font-weight: 400;
	color: #6b7280;
	margin: 0.1rem 0 0;
`;

const Stats = styled.div`
	display: flex;
	gap: 2rem;
	margin-top: 1.5rem;
	flex-wrap: wrap;
	color: ${css_text('weak')};

	@media (${css_small_down}) {
		border-top: 1px solid ${css_stroke()};
		padding-top: ${css_gap('content')};
		margin-top: ${css_gap('content')};
		justify-content: space-between;
	}
`;

const Stat = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1rem;
	font-size: 0.95rem;
`;

const StatIcon = styled.div`
	width: 2rem;
	height: 2rem;
	color: ${css_text('weak')};
`;

const StatValue = styled.div`
	font-size: 1rem;
`;

const Rail = styled.dl`
	display: grid;
	grid-template-columns: 1fr 1fr;
	box-sizing: border-box;
	margin: 0;
	font-size: 0.9rem;
	gap: ${css_gap('content')};
	color: ${css_text('weak')};

	@media (${css_medium_up}) {
		border-left: 1px solid ${css_stroke()};
		padding-left: ${css_gap('content')};
		align-items: center;
	}

	@media (${css_rail_up}) {
		flex-shrink: 0;
		padding: ${css_gap('content')};
		border-right: 1px solid ${css_stroke()};
		width: ${css_rail};
	}

	@media (${css_small_down}) {
		border-top: 1px solid ${css_stroke()};
		padding-top: ${css_gap('content')};
	}
`;

const RailLabel = styled.dt`
	font-weight: 500;
`;

const RailValue = styled.dd`
	text-align: right;
`;

function formatLotSize(lotAcres?: number, lot?: number) {
	if (lotAcres) {
		return `${lotAcres.toFixed(2)} acres`;
	}
	if (lot) {
		return `${lot.toLocaleString()} sqft`;
	}
	return null;
}

export default function ListingBlockAlpha({ id }: { id: string }) {
	const listing = useListingContext();

	return (
		<Section id={id}>
			<Inner>
				<Summary>
					<Title>456 Air Street</Title>
					<Subtitle>Anyville, CA 98445</Subtitle>
					<Stats>
						{listing.beds != null && (
							<Stat>
								<StatIcon>
									<IconBedUltimate />
								</StatIcon>
								<StatValue>{listing.beds} beds</StatValue>
							</Stat>
						)}
						{listing.baths != null && (
							<Stat>
								<StatIcon>
									<IconBathUltimate />
								</StatIcon>
								<StatValue>{listing.baths} baths</StatValue>
							</Stat>
						)}
						{listing.sqft != null && (
							<Stat>
								<StatIcon>
									<IconSqftUltimate />
								</StatIcon>
								<StatValue>{listing.sqft.toLocaleString()} sqft</StatValue>
							</Stat>
						)}
					</Stats>
				</Summary>
				<Rail>
					{(listing.lot ?? listing.lot_acres) != null && (
						<>
							<RailLabel>Lot Size</RailLabel>
							<RailValue>{formatLotSize(listing.lot_acres, listing.lot)}</RailValue>
						</>
					)}
					{listing.garage != null && (
						<>
							<RailLabel>Garage</RailLabel>
							<RailValue>{listing.garage}</RailValue>
						</>
					)}
					{listing.built != null && (
						<>
							<RailLabel>Year Built</RailLabel>
							<RailValue>{listing.built}</RailValue>
						</>
					)}
				</Rail>
			</Inner>
		</Section>
	);
}
